import { useState } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { Home, Search, Plus, Sparkles, LogIn, X, Wrench, UserCog, Skull } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import { useMyProfile } from '@/features/home/queries'
import { useJoinLeague } from '@/features/league/hooks/useUserLeagues'
import { useJoinSurvivorGame } from '@/features/sobrevivente/queries'
import { useIsAdmin } from '@/features/admin/queries'
import { LogoMark, LogoWordmark } from './Logo'
import { useTranslation } from '@/shared/i18n/useTranslation'
import styles from './Sidebar.module.css'

// ─── Join League inline form ──────────────────────────────────────────────────

const CODE_REGEX = /^[A-Z0-9]{3}-[A-Z0-9]{4}$/

function formatCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
  if (clean.length <= 3) return clean
  return `${clean.slice(0, 3)}-${clean.slice(3)}`
}

const GENERIC_JOIN_ERROR = 'Não foi possível entrar. Tente novamente.'

function leagueJoinError(code: string | undefined): string {
  switch (code) {
    case 'invalid_invite_code':   return 'Código inválido.'
    case 'invite_code_not_found': return 'Código não encontrado.'
    case 'already_member':        return 'Você já está nessa liga.'
    case 'league_full':           return 'Liga cheia.'
    case 'league_limit_reached':  return 'Limite de ligas atingido.'
    case 'team_name_taken':       return 'Nome de time já usado nessa liga.'
    default:                      return GENERIC_JOIN_ERROR
  }
}

function survivorJoinError(code: string | undefined): string {
  switch (code) {
    case 'invalid_invite_code':     return 'Código inválido.'
    case 'invite_code_not_found':   return 'Código não encontrado.'
    case 'survivor_already_member': return 'Você já está nesse jogo.'
    case 'survivor_game_full':      return 'Jogo lotado.'
    case 'survivor_game_locked':    return 'Este jogo já começou.'
    default:                        return GENERIC_JOIN_ERROR
  }
}

function JoinForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [teamName, setTeamName] = useState('')
  const [error, setError] = useState('')
  const joinLeague   = useJoinLeague()
  const joinSurvivor = useJoinSurvivorGame()
  const isCodeValid  = CODE_REGEX.test(code)
  const isPending    = joinLeague.isPending || joinSurvivor.isPending

  // One code field serves both products. A league and a survivor game share the
  // XXX-XXXX format, so try a fantasy league first; if the code isn't a league
  // invite, fall back to a survivor game with the same code.
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isCodeValid) {
      setError('Formato: XXX-XXXX')
      return
    }
    setError('')
    try {
      await joinLeague.mutateAsync({ code, teamName: teamName.trim() || undefined })
      onClose() // useJoinLeague navigates to the league on success
    } catch (leagueErr) {
      if (leagueErr instanceof ApiError && leagueErr.errorCode === 'invite_code_not_found') {
        try {
          const result = await joinSurvivor.mutateAsync(code)
          onClose()
          void navigate({ to: '/sobrevivente/$gameId', params: { gameId: result.gameId }, search: { gw: undefined } })
        } catch (survErr) {
          setError(survErr instanceof ApiError ? survivorJoinError(survErr.errorCode) : GENERIC_JOIN_ERROR)
        }
        return
      }
      setError(leagueErr instanceof ApiError ? leagueJoinError(leagueErr.errorCode) : GENERIC_JOIN_ERROR)
    }
  }

  return (
    <form className={styles.joinForm} onSubmit={handleSubmit} noValidate>
      <div className={styles.joinFormRow}>
        <input
          className={clsx(styles.joinInput, error && styles.joinInputError, isCodeValid && styles.joinInputValid)}
          type="text"
          value={code}
          onChange={(e) => { setCode(formatCode(e.target.value)); if (error) setError('') }}
          placeholder="XXX-XXXX"
          maxLength={8}
          autoComplete="off"
          spellCheck={false}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          aria-label="Código de convite (liga ou sobrevivente)"
        />
        <button type="button" className={styles.joinFormClose} onClick={onClose} aria-label="Cancelar">
          <X size={14} aria-hidden="true" />
        </button>
      </div>
      <input
        className={styles.joinInput}
        type="text"
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
        placeholder="Nome do time (opcional)"
        maxLength={30}
        autoComplete="off"
        spellCheck={false}
        aria-label="Nome do seu time (apenas ligas, opcional)"
      />
      {error && <span className={styles.joinFormError} role="alert">{error}</span>}
      <button
        type="submit"
        className={styles.joinFormSubmit}
        disabled={!isCodeValid || isPending}
      >
        {isPending ? 'Entrando…' : 'Entrar'}
      </button>
    </form>
  )
}

function SidebarTierBadge({ onNavigate }: { onNavigate?: () => void }) {
  const { data: profile } = useMyProfile()
  const tier = profile?.subscriptionTier

  // BETA: non-clickable badge (no upsell during beta).
  if (tier === 'BETA') {
    return (
      <div className={styles.tierBadge} aria-label="Plano Beta">
        <Sparkles size={14} className={styles.tierBadgeIcon} aria-hidden="true" />
        <span className={styles.tierBadgeText}>
          <span className={styles.tierBadgeLabel}>Plano Beta</span>
          <span className={styles.tierBadgeHint}>Acesso completo</span>
        </span>
      </div>
    )
  }

  const isFree = !profile || tier === 'FREE'
  if (!isFree) return null

  return (
    <Link to="/subscription" onClick={onNavigate} className={styles.tierBadge} aria-label="Ver planos">
      <Sparkles size={14} className={styles.tierBadgeIcon} aria-hidden="true" />
      <span className={styles.tierBadgeText}>
        <span className={styles.tierBadgeLabel}>Plano Gratuito</span>
        <span className={styles.tierBadgeHint}>Ver planos →</span>
      </span>
    </Link>
  )
}

type SidebarProps = {
  mobileOpen?: boolean
  onClose?:    () => void
}

export function Sidebar({ mobileOpen = false, onClose }: SidebarProps) {
  const { t } = useTranslation()
  const { location } = useRouterState()
  const path = location.pathname
  const { data: adminMe } = useIsAdmin()
  const isAdmin = adminMe?.isAdmin === true
  const [showJoinForm, setShowJoinForm] = useState(false)

  // Mobile drawer dismisses on navigation; no-op on desktop where onClose is absent.
  function handleNavClick() {
    onClose?.()
  }

  return (
    <>
      {mobileOpen && (
        <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      )}
      <aside
        className={clsx(styles.sidebar, mobileOpen && styles.isMobileOpen)}
        aria-label="Navegação principal"
      >
      {/* Mobile-only close button */}
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Fechar menu"
      >
        <X size={18} aria-hidden="true" />
      </button>

      {/* Logo — the Y-on-a-podium mark + FANTYX.PRO wordmark */}
      <div className={styles.logo}>
        <LogoMark size={24} />
        <span className={styles.logoWordmark}>
          <LogoWordmark />
        </span>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        {/* Início */}
        <Link
          to="/home"
          onClick={handleNavClick}
          className={clsx(styles.navItem, path === '/home' && styles.isActive)}
          aria-current={path === '/home' ? 'page' : undefined}
        >
          <Home size={18} className={styles.navIcon} aria-hidden="true" />
          <span className={styles.navLabel}>Início</span>
        </Link>

        {/* "Suas Ligas" is deliberately absent: classic fantasy is frozen, so it
            is off the menus. The /ligas and /league/* routes still work for
            anyone holding a link, and keep their old Voltage look. */}

        {/* Sobrevivente */}
        <Link
          to="/sobrevivente"
          onClick={handleNavClick}
          className={clsx(styles.navItem, path.startsWith('/sobrevivente') && styles.isActive)}
          aria-current={path.startsWith('/sobrevivente') ? 'page' : undefined}
        >
          <Skull size={18} className={styles.navIcon} aria-hidden="true" />
          <span className={styles.navLabel}>Sobrevivente</span>
        </Link>

        {/* Admin — gated by /v1/admin/me. Two sibling pages: Rodadas (index)
            and Pessoas. Active state is split so only one highlights at a time. */}
        {isAdmin && (
          <>
            <Link
              to="/admin"
              onClick={handleNavClick}
              className={clsx(
                styles.navItem,
                (path === '/admin' || path.startsWith('/admin/gameweek')) && styles.isActive,
              )}
              aria-current={path === '/admin' || path.startsWith('/admin/gameweek') ? 'page' : undefined}
            >
              <Wrench size={18} className={styles.navIcon} aria-hidden="true" />
              <span className={styles.navLabel}>Admin</span>
            </Link>
            <Link
              to="/admin/persons"
              onClick={handleNavClick}
              className={clsx(styles.navItem, path.startsWith('/admin/persons') && styles.isActive)}
              aria-current={path.startsWith('/admin/persons') ? 'page' : undefined}
            >
              <UserCog size={18} className={styles.navIcon} aria-hidden="true" />
              <span className={styles.navLabel}>Pessoas</span>
            </Link>
          </>
        )}

        {/* Descobrir — feature not built yet */}
        <span className={clsx(styles.navItem, styles.isDimmed)} aria-disabled="true">
          <Search size={18} className={styles.navIcon} aria-hidden="true" />
          <span className={styles.navLabel}>Descobrir</span>
          <span className={styles.soonBadge}>Soon</span>
        </span>
      </nav>

      {/* Bottom: tier badge + create CTA + join CTA */}
      <div className={styles.bottom}>
        <SidebarTierBadge onNavigate={handleNavClick} />
        {showJoinForm && <JoinForm onClose={() => setShowJoinForm(false)} />}
        <Link
          to="/sobrevivente/new"
          onClick={handleNavClick}
          className={styles.createBtn}
          aria-label={t('nav.createGame')}
        >
          <Plus size={16} aria-hidden="true" />
          <span className={styles.createBtnText}>{t('nav.createGame')}</span>
        </Link>
        <button
          type="button"
          className={styles.joinBtn}
          aria-label={t('nav.joinWithCode')}
          onClick={() => setShowJoinForm((v) => !v)}
        >
          <LogIn size={16} aria-hidden="true" />
          <span className={styles.joinBtnText}>{t('nav.joinWithCode')}</span>
        </button>
      </div>
      </aside>
    </>
  )
}
